<template>
  <div class="agent-patient-management">
    <b-container fluid>
      <b-card class="border-0 shadow-sm">
        <template #header>
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div>
              <h2 class="h4 text-primary mb-1">{{ $t('Patient Management') }}</h2>
              <p class="mb-0 text-muted">
                Manage and create patients assigned to your portfolio
              </p>
            </div>
            <div class="d-flex flex-wrap align-items-center gap-2 mt-3 mt-md-0">
              <b-button
                variant="outline-primary"
                :href="bookingUrl"
                target="_blank"
                rel="noopener noreferrer">
                <i class="flaticon2-calendar mr-2"></i>
                <span>Request appointment</span>
              </b-button>
              <b-button
                variant="primary"
                :disabled="loading"
                @click="openCreateModal">
                <i class="fas fa-plus mr-2"></i>
                <span>{{ $t('Add') }} {{ $t('Patient') }}</span>
              </b-button>
            </div>
          </div>
        </template>

        <!-- Tabla de pacientes -->
        <div class="mt-4">
          <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
            <SpinnerLoading />
          </div>

          <div v-else>
            <b-table
              :items="patients"
              :fields="tableFields"
              hover
              bordered
              responsive
              primary-key="id"
              class="position-relative"
              show-empty
              empty-text="No records found">
              
              <template #cell(patient)="data">
                <b-media vertical-align="center">
                  <button
                    type="button"
                    class="btn btn-link font-weight-bold d-block text-nowrap p-0 text-left"
                    @click="openAppointments(data.item)">
                    {{ data.item.name }} {{ data.item.lastname }}
                  </button>
                  <small class="text-muted">{{ data.item.email || 'No email' }}</small>
                </b-media>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="data.item.status ? 'success' : 'danger'" class="badge-glow">
                  <span class="align-text-top text-capitalize">
                    {{ data.item.status ? $t('Active') : $t('Inactive') }}
                  </span>
                </b-badge>
              </template>

              <template #cell(phone)="data">
                <div class="text-nowrap">
                  <span v-if="data.item.phone">{{ formatPhone(data.item.phone) }}</span>
                  <span v-else class="text-muted">-</span>
                </div>
              </template>

              <template #cell(name_insurance)="data">
                <div v-if="data.item.name_insurance" class="text-nowrap">
                  <b-badge variant="info" class="badge-glow">
                    {{ data.item.name_insurance.name }}
                  </b-badge>
                </div>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(date_birth)="data">
                <span>{{ data.item.date_birth || '-' }}</span>
              </template>

              <template #cell(actions)="data">
                <div class="d-flex flex-wrap align-items-center" style="gap: 0.35rem;">
                  <b-button
                    size="sm"
                    variant="outline-info"
                    @click="openAppointments(data.item)">
                    <i class="fas fa-eye mr-1"></i>
                    View detail
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    @click="openEditModal(data.item)">
                    <i class="fas fa-pen mr-1"></i>
                    {{ $t('Edit') }}
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :href="bookingUrl"
                    target="_blank"
                    rel="noopener noreferrer">
                    Request appointment
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </b-card>
    </b-container>

    <b-modal
      v-model="appointmentsVisible"
      size="xl"
      centered
      scrollable
      ok-only
      ok-variant="primary"
      ok-title="Close"
      modal-class="agent-appointments-modal"
      :title="appointmentsTitle()">
      <div v-if="appointmentsLoading" class="d-flex justify-content-center py-4">
        <SpinnerLoading />
      </div>
      <b-table
        v-else
        :items="appointments"
        :fields="appointmentFields"
        striped
        hover
        bordered
        responsive
        class="agent-appointments-table text-center mb-0"
        show-empty
        empty-text="No appointments found">
        <template #cell(type_visit)="data">
          <b-badge
            v-if="data.item.type_visits"
            v-b-tooltip.hover.top="data.item.type_visits.name"
            class="badge-glow visit-type-badge"
            :style="{
              backgroundColor: data.item.type_visits.color || '#6c757d',
              color: visitTypeTextColor(data.item.type_visits.color),
            }">
            {{ data.item.type_visits.prefix || data.item.type_visits.name }}
          </b-badge>
          <span v-else class="text-muted">-</span>
        </template>
        <template #cell(visit_date)="data">
          <span class="visit-date-pill">{{ data.item.visit_date || '-' }}</span>
        </template>
        <template #cell(visit_reason)="data">
          <span class="visit-reason-pill">{{ visitReasonLabel(data.item) }}</span>
        </template>
        <template #cell(facility)="data">
          <span class="facility-label">
            <i class="far fa-hospital mr-1"></i>
            {{ data.item.facilities && data.item.facilities.name ? data.item.facilities.name : '-' }}
          </span>
        </template>
        <template #cell(frontdesk)="data">
          <b-media
            v-if="data.item.frontdesks"
            vertical-align="center"
            class="d-inline-flex text-left">
            <template #aside>
              <b-avatar size="32" :src="data.item.frontdesks.avatar" />
            </template>
            <span class="font-weight-bold text-nowrap">
              {{ frontdeskLabel(data.item) }}
            </span>
          </b-media>
          <span v-else class="text-muted">-</span>
        </template>
      </b-table>
    </b-modal>

    <!-- Modal para crear paciente (centrado) -->
    <b-modal
      id="create-patient-modal"
      v-model="isModalVisible"
      title-class="font-weight-bold text-primary"
      size="xl"
      dialog-class="modal-agent-patient-wide"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      @hidden="resetForm">
      <template #modal-title>
        <i class="fas fa-user-plus mr-2"></i>
        {{ $t('Add') }} {{ $t('Patient') }}
      </template>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <b-row>
            <!-- Nombre -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Name"
                rules="required">
                <b-form-group label-for="name">
                  <label for="name">
                    {{ $t('Form.Name') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="name"
                    v-model="formData.name"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Name"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Apellido -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Last Name"
                rules="required">
                <b-form-group label-for="lastname">
                  <label for="lastname">
                    {{ $t('Form.Last Name') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="lastname"
                    v-model="formData.lastname"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Last Name"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Fecha de nacimiento -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Date of Birth"
                rules="required">
                <b-form-group label-for="date_birth">
                  <label for="date_birth">
                    DOB <span class="text-danger">*</span>
                  </label>
                  <div class="flatpickr-wrapper">
                    <flat-pickr
                      v-model="formData.date_birth"
                      :config="dateConfig"
                      class="form-control"
                      placeholder="MM-DD-YYYY"
                      :class="{
                        'is-invalid': getValidationState(validationContext) === false,
                      }"
                      @on-change="() => { validationContext.touched = true; validationContext.validate(); }"
                      @on-close="() => { validationContext.touched = true; validationContext.validate(); }"
                      @blur="validationContext.validate" />
                  </div>
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Teléfono -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Phone"
                rules="required">
                <b-form-group label-for="phone">
                  <label for="phone">
                    {{ $t('Form.Phone') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="phone"
                    v-model="formData.phone"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="(000) 000-0000"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Género -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Gender"
                rules="required">
                <b-form-group label-for="gender">
                  <label for="gender">
                    {{ $t('Form.Gender') }} <span class="text-danger">*</span>
                  </label>
                  <treeselect
                    id="gender"
                    v-model="formData.gender"
                    :options="genderOptions"
                    :normalizer="normalizer"
                    placeholder="Select gender"
                    :class="{
                      'custom-treeselect': true,
                      'is-invalid': getValidationState(validationContext) === false
                    }"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Tipo de Seguro -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Insurance Type"
                rules="required">
                <b-form-group label-for="nameInsuranceId">
                  <label for="nameInsuranceId">
                    {{ $t('Name insurance') }} <span class="text-danger">*</span>
                  </label>
                  <treeselect
                    id="nameInsuranceId"
                    v-model="formData.nameInsuranceId"
                    :options="insuranceOptions"
                    :normalizer="normalizer"
                    placeholder="Select insurance"
                    :class="{
                      'custom-treeselect': true,
                      'is-invalid': getValidationState(validationContext) === false
                    }"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Referencia -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Reference"
                rules="required">
                <b-form-group label-for="user_references_id">
                  <label for="user_references_id">
                    {{ $t('Reference') }} <span class="text-danger">*</span>
                  </label>
                  <treeselect
                    id="user_references_id"
                    v-model="formData.user_references_id"
                    :options="referenceOptions"
                    :normalizer="normalizer"
                    placeholder="Select reference"
                    :class="{
                      'custom-treeselect': true,
                      'is-invalid': getValidationState(validationContext) === false
                    }"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Email (opcional) -->
            <b-col cols="12" md="3">
              <b-form-group label-for="create-email" label="Email">
                <b-form-input
                  id="create-email"
                  v-model="formData.email"
                  type="email"
                  trim
                  placeholder="email@example.com" />
              </b-form-group>
            </b-col>

            <!-- Copay -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Copay"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="copay">
                  <label for="copay">
                    {{ $t('Copay') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="copay"
                    v-model="formData.copay"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Co-Insurance -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Co Insurance"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="co_insurance">
                  <label for="co_insurance">
                    {{ $t('CoInsurance') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="co_insurance"
                    v-model="formData.co_insurance"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Deducible -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Deducible"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="deducible">
                  <label for="deducible">
                    {{ $t('Deducible') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="deducible"
                    v-model="formData.deducible"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Out of Pocket -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Out Pocket"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="out_pocket">
                  <label for="out_pocket">
                    {{ $t('OutPocket') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="out_pocket"
                    v-model="formData.out_pocket"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Member ID -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Member ID"
                rules="required">
                <b-form-group label-for="member_id">
                  <label for="member_id">
                    Member ID <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="member_id"
                    v-model="formData.member_id"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Member ID"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- PCP change confirmation number -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="PCP change confirmation number"
                rules="required">
                <b-form-group label-for="confirm_number">
                  <label for="confirm_number">
                    PCP change confirmation number <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="confirm_number"
                    v-model="formData.confirm_number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="PCP change confirmation number"
                    @blur="validationContext.validate" />
                  <small class="form-text text-info">
                    <i class="fas fa-info-circle mr-1"></i>
                    Si no lo sabes coloca NA
                  </small>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Address, Zip y City al final (misma UX que PatientAdd.vue) -->
            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="Address"
                rules="required">
                <b-form-group label-for="create-address">
                  <label for="create-address">
                    Address <span class="text-danger">*</span>
                    <small class="text-muted">({{ $t('Required') }})</small>
                  </label>
                  <b-form-input
                    id="create-address"
                    v-model="formData.address"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Enter address"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="Zip Code"
                rules="required">
                <b-form-group label="Zip Code" label-for="create-zip">
                  <b-form-input
                    id="create-zip"
                    v-model="formData.zip_codes_id"
                    type="number"
                    :state="getValidationState(validationContext)"
                    placeholder="Enter zip code"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="City"
                rules="required">
                <b-form-group label-for="create-city" class="city-autocomplete-wrap">
                  <template #label>
                    {{ $t('Form.City') }} <span class="text-danger">*</span>
                    <small class="text-muted">({{ $t('Required') }})</small>
                  </template>
                  <small class="d-block text-info mb-1">
                    <i class="fas fa-info-circle mr-1"></i>
                    Escriba el nombre de la ciudad y luego
                    <strong>debe hacer clic en una opción de la lista</strong> para seleccionarla.
                  </small>
                  <b-form-input
                    id="create-city"
                    v-model="createCityName"
                    :state="formData.cities_id ? true : createCityName ? false : null"
                    placeholder="Escriba y seleccione de la lista desplegable"
                    @input="searchCitiesFromInputCreate"
                    @focus="createCityName && searchCitiesFromInputCreate()"
                    @blur="validationContext.validate" />
                  <div v-if="createCityOptions.length" class="city-options-list">
                    <div
                      v-for="opt in createCityOptions"
                      :key="'c-' + opt.value"
                      class="city-option-item"
                      @mousedown.prevent="selectCityOptionCreate(opt)">
                      <i class="fas fa-mouse-pointer mr-1 text-muted small"></i>{{ opt.text }}
                    </div>
                  </div>
                  <b-form-invalid-feedback v-if="createCityName && !formData.cities_id">
                    Debe seleccionar una ciudad de la lista haciendo clic en ella.
                  </b-form-invalid-feedback>
                  <b-form-invalid-feedback v-else>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>

      <template #modal-footer="{ cancel }">
        <b-button variant="outline-secondary" @click="cancel">
          {{ $t('Cancel') }}
        </b-button>
        <b-button variant="primary" :disabled="submitting" @click="handleSubmitForm">
          <span v-if="!submitting">{{ $t('Add') }}</span>
          <span v-else><SpinnerLoading /></span>
        </b-button>
      </template>
    </b-modal>

    <!-- Editar paciente: nombre completo y DOB solo lectura -->
    <b-modal
      id="edit-patient-modal"
      v-model="isEditModalVisible"
      title-class="font-weight-bold text-primary"
      size="xl"
      dialog-class="modal-agent-patient-wide"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      @hidden="resetEditForm">
      <template #modal-title>
        <i class="fas fa-user-edit mr-2"></i>
        {{ $t('Edit') }} {{ $t('Patient') }}
      </template>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserverEdit">
        <b-form @submit.prevent="handleSubmit(onSubmitEdit)" @reset.prevent="resetEditForm">
          <b-row>
            <b-col cols="12" md="3">
              <b-form-group :label="$t('Form.Name')">
                <b-form-input :value="editFormData.name" readonly plaintext />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group :label="$t('Form.Last Name')">
                <b-form-input :value="editFormData.lastname" readonly plaintext />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="3">
              <b-form-group label="DOB">
                <b-form-input :value="editFormData.date_birth" readonly plaintext />
              </b-form-group>
            </b-col>

            <!-- Email (opcional) -->
            <b-col cols="12" md="3">
              <b-form-group label-for="edit-email" label="Email">
                <b-form-input
                  id="edit-email"
                  v-model="editFormData.email"
                  type="email"
                  trim
                  placeholder="email@example.com" />
              </b-form-group>
            </b-col>

            <!-- Teléfono -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Phone"
                rules="required">
                <b-form-group label-for="edit-phone">
                  <label for="edit-phone">
                    {{ $t('Form.Phone') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-phone"
                    v-model="editFormData.phone"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="(000) 000-0000"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Género -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Gender"
                rules="required">
                <b-form-group label-for="edit-gender">
                  <label for="edit-gender">
                    {{ $t('Form.Gender') }} <span class="text-danger">*</span>
                  </label>
                  <treeselect
                    id="edit-gender"
                    v-model="editFormData.gender"
                    :options="genderOptions"
                    :normalizer="normalizer"
                    placeholder="Select gender"
                    :class="{
                      'custom-treeselect': true,
                      'is-invalid': getValidationState(validationContext) === false,
                    }"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Seguro -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Insurance Type"
                rules="required">
                <b-form-group label-for="edit-nameInsuranceId">
                  <label for="edit-nameInsuranceId">
                    {{ $t('Name insurance') }} <span class="text-danger">*</span>
                  </label>
                  <treeselect
                    id="edit-nameInsuranceId"
                    v-model="editFormData.nameInsuranceId"
                    :options="insuranceOptions"
                    :normalizer="normalizer"
                    placeholder="Select insurance"
                    :class="{
                      'custom-treeselect': true,
                      'is-invalid': getValidationState(validationContext) === false,
                    }"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Referencia -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Reference"
                rules="required">
                <b-form-group label-for="edit-user_references_id">
                  <label for="edit-user_references_id">
                    {{ $t('Reference') }} <span class="text-danger">*</span>
                  </label>
                  <treeselect
                    id="edit-user_references_id"
                    v-model="editFormData.user_references_id"
                    :options="referenceOptions"
                    :normalizer="normalizer"
                    placeholder="Select reference"
                    :class="{
                      'custom-treeselect': true,
                      'is-invalid': getValidationState(validationContext) === false,
                    }"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Copay -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Copay"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="edit-copay">
                  <label for="edit-copay">
                    {{ $t('Copay') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-copay"
                    v-model="editFormData.copay"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Co-Insurance -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Co Insurance"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="edit-co_insurance">
                  <label for="edit-co_insurance">
                    {{ $t('CoInsurance') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-co_insurance"
                    v-model="editFormData.co_insurance"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Deducible -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Deducible"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="edit-deducible">
                  <label for="edit-deducible">
                    {{ $t('Deducible') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-deducible"
                    v-model="editFormData.deducible"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Out of Pocket -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Out Pocket"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="edit-out_pocket">
                  <label for="edit-out_pocket">
                    {{ $t('OutPocket') }} <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-out_pocket"
                    v-model="editFormData.out_pocket"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Member ID -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="Member ID"
                rules="required">
                <b-form-group label-for="edit-member_id">
                  <label for="edit-member_id">
                    Member ID <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-member_id"
                    v-model="editFormData.member_id"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Member ID"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- PCP change confirmation number -->
            <b-col cols="12" md="3">
              <validation-provider
                #default="validationContext"
                name="PCP change confirmation number"
                rules="required">
                <b-form-group label-for="edit-confirm_number">
                  <label for="edit-confirm_number">
                    PCP change confirmation number <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    id="edit-confirm_number"
                    v-model="editFormData.confirm_number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="PCP change confirmation number"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Address, Zip y City al final (misma UX que PatientAdd.vue) -->
            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="Address"
                rules="required">
                <b-form-group label-for="edit-address">
                  <label for="edit-address">
                    Address <span class="text-danger">*</span>
                    <small class="text-muted">({{ $t('Required') }})</small>
                  </label>
                  <b-form-input
                    id="edit-address"
                    v-model="editFormData.address"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Enter address"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="Zip Code"
                rules="required">
                <b-form-group label="Zip Code" label-for="edit-zip">
                  <b-form-input
                    id="edit-zip"
                    v-model="editFormData.zip_codes_id"
                    type="number"
                    :state="getValidationState(validationContext)"
                    placeholder="Enter zip code"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="City"
                rules="required">
                <b-form-group label-for="edit-city" class="city-autocomplete-wrap">
                  <template #label>
                    {{ $t('Form.City') }} <span class="text-danger">*</span>
                    <small class="text-muted">({{ $t('Required') }})</small>
                  </template>
                  <small class="d-block text-info mb-1">
                    <i class="fas fa-info-circle mr-1"></i>
                    Escriba el nombre de la ciudad y luego
                    <strong>debe hacer clic en una opción de la lista</strong> para seleccionarla.
                  </small>
                  <b-form-input
                    id="edit-city"
                    v-model="editCityName"
                    :state="editFormData.cities_id ? true : editCityName ? false : null"
                    placeholder="Escriba y seleccione de la lista desplegable"
                    @input="searchCitiesFromInputEdit"
                    @focus="editCityName && searchCitiesFromInputEdit()"
                    @blur="validationContext.validate" />
                  <div v-if="editCityOptions.length" class="city-options-list">
                    <div
                      v-for="opt in editCityOptions"
                      :key="'e-' + opt.value"
                      class="city-option-item"
                      @mousedown.prevent="selectCityOptionEdit(opt)">
                      <i class="fas fa-mouse-pointer mr-1 text-muted small"></i>{{ opt.text }}
                    </div>
                  </div>
                  <b-form-invalid-feedback v-if="editCityName && !editFormData.cities_id">
                    Debe seleccionar una ciudad de la lista haciendo clic en ella.
                  </b-form-invalid-feedback>
                  <b-form-invalid-feedback v-else>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>

      <template #modal-footer="{ cancel }">
        <b-button variant="outline-secondary" @click="cancel">
          {{ $t('Cancel') }}
        </b-button>
        <b-button variant="primary" :disabled="submittingEdit" @click="handleSubmitEditForm">
          <span v-if="!submittingEdit">{{ $t('Save') }}</span>
          <span v-else><SpinnerLoading /></span>
        </b-button>
      </template>
    </b-modal>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BContainer,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BTable,
  BBadge,
  BMedia,
  BAvatar,
  BModal,
  BRow,
  BCol,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import flatPickr from 'vue-flatpickr-component'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { required } from '@validations'
import formValidation from '@/core/utils/forms/form-validation'
import SpinnerLoading from '@/components/SpinnerLoading'
import ToastNotification from '@/components/ToastNotification'
import axiosPatient from '@/core/services/apiInsurance/patient'
import axiosAppointments from '@/core/services/apiInsurance/patient/appointment'
import axiosNI from '@/core/services/apiInsurance/admin/consults/insuranceName'
import axiosC from '@/core/services/apiInsurance/admin/cities'
import { notificationCheck } from '@/mixins/NotificationSetup'
import {
  appointmentsTitle as formatAppointmentsTitle,
  frontdeskLabel,
  loadPatientAppointments,
  visitReasonLabel,
  visitTypeTextColor,
} from '@/view/pages/insuranceAgent/appointmentDisplay'
import { INSURANCE_AGENT_BOOKING_URL } from '@/view/pages/insuranceAgent/bookingRedirect'

export default {
  name: 'AgentPatientManagement',
  components: {
    BButton,
    BCard,
    BContainer,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BTable,
    BBadge,
    BMedia,
    BAvatar,
    BModal,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    flatPickr,
    Treeselect,
    SpinnerLoading,
    ToastNotification,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      required,
      bookingUrl: INSURANCE_AGENT_BOOKING_URL,
      loading: false,
      appointmentsVisible: false,
      appointmentsLoading: false,
      appointmentsPatient: null,
      appointments: [],
      appointmentFields: [
        { key: 'type_visit', label: 'Type Visit' },
        { key: 'visit_date', label: 'Visit Date' },
        { key: 'visit_reason', label: 'Visit Reason' },
        { key: 'facility', label: 'Facility' },
        { key: 'frontdesk', label: 'Frontdesk' },
      ],
      submitting: false,
      submittingEdit: false,
      isModalVisible: false,
      isEditModalVisible: false,
      editingPatientId: null,
      patients: [],
      createCityName: '',
      createCityOptions: [],
      createCitySearchTimer: null,
      editCityName: '',
      editCityOptions: [],
      editCitySearchTimer: null,
      citySearchAccessDeniedToastShown: false,
      formData: {
        name: '',
        lastname: '',
        phone: '',
        date_birth: '',
        email: '',
        gender: null,
        nameInsuranceId: null,
        user_references_id: null,
        address: '',
        cities_id: null,
        zip_codes_id: '',
        copay: '',
        co_insurance: '',
        deducible: '',
        out_pocket: '',
        member_id: '',
        confirm_number: '',
      },
      editFormData: {
        name: '',
        lastname: '',
        date_birth: '',
        email: '',
        phone: '',
        gender: null,
        nameInsuranceId: null,
        user_references_id: null,
        address: '',
        cities_id: null,
        zip_codes_id: '',
        copay: '',
        co_insurance: '',
        deducible: '',
        out_pocket: '',
        member_id: '',
        confirm_number: '',
      },
      genderOptions: [
        { id: 'male', name: 'Male' },
        { id: 'female', name: 'Female' },
      ],
      insuranceOptions: [],
      referenceOptions: [],
      dateConfig: {
        dateFormat: 'm-d-Y',
        allowInput: true,
        static: true,
      },
      tableFields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'patient', label: 'Patient', sortable: true },
        { key: 'phone', label: 'Phone', sortable: true },
        { key: 'date_birth', label: 'DOB', sortable: true },
        { key: 'gender', label: 'Gender', sortable: true },
        { key: 'name_insurance', label: 'Insurance', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
    }
  },
  mounted() {
    this.loadPatients()
    this.loadInsuranceOptions()
    this.loadReferenceOptions()
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.name,
      }
    },
    appointmentsTitle() {
      return formatAppointmentsTitle(this.appointmentsPatient)
    },
    visitTypeTextColor,
    visitReasonLabel,
    frontdeskLabel,
    async openAppointments(patient) {
      this.appointmentsPatient = patient
      this.appointments = []
      this.appointmentsVisible = true
      this.appointmentsLoading = true
      try {
        const { rows, unavailable } = await loadPatientAppointments(
          patient.id,
          axiosAppointments.appointmentListPage,
        )
        if (unavailable && this.$refs.toast) {
          this.$refs.toast.danger('Could not load appointments.')
        }
        this.appointments = rows
      } catch (error) {
        this.appointments = []
        const { type, message } = notificationCheck(error && error.response)
        this.$refs.toast[type](message)
      } finally {
        this.appointmentsLoading = false
      }
    },
    async loadPatients() {
      this.loading = true
      try {
        const response = await axiosPatient.getAgentPatients()
        const raw = response && response.data !== undefined ? response.data : response
        if (Array.isArray(raw)) {
          this.patients = raw
        } else if (raw && Array.isArray(raw.data)) {
          this.patients = raw.data
        } else {
          this.patients = []
        }
      } catch (error) {
        const { type, message } = notificationCheck(error.response)
        this.$refs.toast[type](message)
      } finally {
        this.loading = false
      }
    },
    async loadInsuranceOptions() {
      try {
        const { data } = await axiosNI.visitList(100)
        this.insuranceOptions = data || []
      } catch (error) {
        console.error('Error loading insurance options:', error)
      }
    },
    async loadReferenceOptions() {
      try {
        const { data } = await axiosPatient.referencePagination(100, 1)
        this.referenceOptions = data.data || []
      } catch (error) {
        console.error('Error loading reference options:', error)
      }
    },
    openCreateModal() {
      this.citySearchAccessDeniedToastShown = false
      this.isModalVisible = true
    },
    /** Respuesta de POST states/cities/search-city: array o { data } o un único { id, name } */
    normalizeCitySearchResponse(response) {
      if (!response) return []
      if (Array.isArray(response)) return response
      if (response.data != null) {
        if (Array.isArray(response.data)) return response.data
        if (
          typeof response.data === 'object' &&
          response.data.id != null &&
          response.data.name
        ) {
          return [response.data]
        }
      }
      if (response.id != null && response.name) return [response]
      return []
    },
    searchCitiesFromInputCreate() {
      if (!this.createCityName || !this.createCityName.length) {
        this.createCityOptions = []
        return
      }
      clearTimeout(this.createCitySearchTimer)
      this.createCitySearchTimer = setTimeout(() => {
        axiosC
          .citiesSearchCity({ name: this.createCityName })
          .then(response => {
            if (
              response &&
              typeof response === 'object' &&
              response.message &&
              !Array.isArray(response) &&
              !(response.data && Array.isArray(response.data))
            ) {
              this.createCityOptions = []
              this.notifyCitySearchAccessDenied(response.message)
              return
            }
            const list = this.normalizeCitySearchResponse(response)
            this.createCityOptions = (list || []).map(city => ({
              text: city.name,
              value: city.id,
            }))
          })
          .catch(error => {
            this.createCityOptions = []
            const status = error.response && error.response.status
            const msg = error.response && error.response.data && error.response.data.message
            if (status === 403 || status === 401) {
              this.notifyCitySearchAccessDenied(msg)
            }
          })
      }, 350)
    },
    notifyCitySearchAccessDenied(message) {
      if (this.citySearchAccessDeniedToastShown) return
      this.citySearchAccessDeniedToastShown = true
      const text =
        message ||
        'No tiene permisos para buscar ciudades. Es necesario habilitar el endpoint en el backend para su rol.'
      if (this.$refs.toast) {
        this.$refs.toast.error(text)
      }
    },
    selectCityOptionCreate(opt) {
      this.formData.cities_id = opt.value
      this.createCityName = opt.text
      this.createCityOptions = []
    },
    searchCitiesFromInputEdit() {
      if (!this.editCityName || !this.editCityName.length) {
        this.editCityOptions = []
        return
      }
      clearTimeout(this.editCitySearchTimer)
      this.editCitySearchTimer = setTimeout(() => {
        axiosC
          .citiesSearchCity({ name: this.editCityName })
          .then(response => {
            if (
              response &&
              typeof response === 'object' &&
              response.message &&
              !Array.isArray(response) &&
              !(response.data && Array.isArray(response.data))
            ) {
              this.editCityOptions = []
              this.notifyCitySearchAccessDenied(response.message)
              return
            }
            const list = this.normalizeCitySearchResponse(response)
            this.editCityOptions = (list || []).map(city => ({
              text: city.name,
              value: city.id,
            }))
          })
          .catch(error => {
            this.editCityOptions = []
            const status = error.response && error.response.status
            const msg = error.response && error.response.data && error.response.data.message
            if (status === 403 || status === 401) {
              this.notifyCitySearchAccessDenied(msg)
            }
          })
      }, 350)
    },
    selectCityOptionEdit(opt) {
      this.editFormData.cities_id = opt.value
      this.editCityName = opt.text
      this.editCityOptions = []
    },
    pickFirstNumericString(obj, keys) {
      if (!obj || typeof obj !== 'object') return ''
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i]
        if (!Object.prototype.hasOwnProperty.call(obj, k)) continue
        const v = obj[k]
        if (v === undefined || v === null || v === '') continue
        return String(v)
      }
      return ''
    },
    extractCityMeta(row) {
      if (!row || typeof row !== 'object') return { cityId: null, cityNameHint: '' }
      const cityNameHint =
        row.city_name ||
        row.cityName ||
        (row.cities && row.cities.name) ||
        (row.city && row.city.name) ||
        ''
      let cityId =
        row.cities_id != null && row.cities_id !== '' ? row.cities_id : null
      if (cityId == null) cityId = row.city_id != null && row.city_id !== '' ? row.city_id : null
      if (cityId == null && row.cities && row.cities.id != null) cityId = row.cities.id
      if (cityId == null && row.city && row.city.id != null) cityId = row.city.id
      return {
        cityId: cityId != null && cityId !== '' ? Number(cityId) : null,
        cityNameHint: cityNameHint ? String(cityNameHint) : '',
      }
    },
    async hydrateEditCityDisplayName(cityId, cityNameHint = '') {
      this.editCityName = ''
      if (cityNameHint) {
        this.editCityName = cityNameHint
        return
      }
      if (!cityId) return
      try {
        const res = await axiosC.citiesList(2000)
        const list = Array.isArray(res) ? res : (res && res.data) || []
        const found = list.find(c => Number(c.id) === Number(cityId))
        if (found && found.name) this.editCityName = found.name
      } catch (e) {
        console.warn('City label hydrate', e)
      }
    },
    formatDobForDisplay(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'string') return value
      if (value instanceof Date) {
        const m = String(value.getMonth() + 1).padStart(2, '0')
        const d = String(value.getDate()).padStart(2, '0')
        const y = value.getFullYear()
        return `${m}-${d}-${y}`
      }
      return String(value)
    },
    async openEditModal(item) {
      this.citySearchAccessDeniedToastShown = false
      this.editingPatientId = item.id
      const row = item
      const phoneRaw = row.phone ? String(row.phone).replace(/\D/g, '') : ''
      const typeInsRaw =
        row.type_insurance ?? row.typeInsurance ?? row.name_insurance?.id ?? row.insurance_id
      const { cityId, cityNameHint } = this.extractCityMeta(row)
      const copay = this.pickFirstNumericString(row, ['copay', 'co_pay'])
      const coIns = this.pickFirstNumericString(row, [
        'co_insurance',
        'coInsurance',
        'coinsurance',
      ])
      const ded = this.pickFirstNumericString(row, ['deducible', 'deductible'])
      const outP = this.pickFirstNumericString(row, ['out_pocket', 'outPocket', 'out_of_pocket'])
      this.editFormData = {
        name: row.name || '',
        lastname: row.lastname || '',
        date_birth: this.formatDobForDisplay(row.date_birth),
        email: row.email || '',
        phone: phoneRaw,
        gender: row.gender || null,
        nameInsuranceId:
          typeInsRaw != null && typeInsRaw !== '' ? Number(typeInsRaw) : null,
        user_references_id:
          row.user_references_id != null && row.user_references_id !== ''
            ? Number(row.user_references_id)
            : null,
        copay,
        co_insurance: coIns,
        deducible: ded,
        out_pocket: outP,
        member_id: row.member_id != null && row.member_id !== '' ? String(row.member_id) : '',
        address: row.address || '',
        cities_id: cityId,
        zip_codes_id:
          row.zip_codes_id != null && row.zip_codes_id !== '' ? row.zip_codes_id : '',
        confirm_number:
          row.confirm_number != null && row.confirm_number !== ''
            ? String(row.confirm_number)
            : '',
      }
      this.editCityOptions = []
      await this.hydrateEditCityDisplayName(this.editFormData.cities_id, cityNameHint)
      this.isEditModalVisible = true
    },
    resetEditForm() {
      this.citySearchAccessDeniedToastShown = false
      this.editingPatientId = null
      this.editFormData = {
        name: '',
        lastname: '',
        date_birth: '',
        email: '',
        phone: '',
        gender: null,
        nameInsuranceId: null,
        user_references_id: null,
        address: '',
        cities_id: null,
        zip_codes_id: '',
        copay: '',
        co_insurance: '',
        deducible: '',
        out_pocket: '',
        member_id: '',
        confirm_number: '',
      }
      this.editCityName = ''
      this.editCityOptions = []
      if (this.$refs.refFormObserverEdit) {
        this.$refs.refFormObserverEdit.reset()
      }
    },
    async handleSubmitEditForm() {
      const isValid = await this.$refs.refFormObserverEdit.validate()
      if (!isValid) {
        this.$refs.toast.error('Please complete all required fields')
        return
      }
      if (!this.editFormData.cities_id) {
        this.$refs.toast.error('Seleccione una ciudad de la lista.')
        return
      }
      await this.onSubmitEdit()
    },
    async onSubmitEdit() {
      if (!this.editingPatientId) return
      this.submittingEdit = true
      try {
        const payload = {
          phone: this.editFormData.phone.replace(/[() -]/g, ''),
          email: this.editFormData.email || null,
          address: this.editFormData.address || null,
          city: this.editFormData.cities_id,
          zip_codes_id: this.editFormData.zip_codes_id,
          gender: this.editFormData.gender,
          nameInsuranceId: this.editFormData.nameInsuranceId,
          user_references_id: this.editFormData.user_references_id,
          copay: Number(this.editFormData.copay),
          co_insurance: Number(this.editFormData.co_insurance),
          deducible: Number(this.editFormData.deducible),
          out_pocket: Number(this.editFormData.out_pocket),
          member_id: this.editFormData.member_id,
          confirm_number: this.editFormData.confirm_number,
        }

        const response = await axiosPatient.updatePatientByAgent(
          this.editingPatientId,
          payload
        )
        const { type, message } = notificationCheck(response)
        this.$refs.toast[type](message)

        if (type !== 'error') {
          this.isEditModalVisible = false
          this.resetEditForm()
          this.loadPatients()
        }
      } catch (error) {
        const { type, message } = notificationCheck(error.response)
        this.$refs.toast[type](message)
      } finally {
        this.submittingEdit = false
      }
    },
    resetForm() {
      this.citySearchAccessDeniedToastShown = false
      this.createCityName = ''
      this.createCityOptions = []
      this.formData = {
        name: '',
        lastname: '',
        phone: '',
        date_birth: '',
        email: '',
        gender: null,
        nameInsuranceId: null,
        user_references_id: null,
        address: '',
        cities_id: null,
        zip_codes_id: '',
        copay: '',
        co_insurance: '',
        deducible: '',
        out_pocket: '',
        member_id: '',
        confirm_number: '',
      }
      if (this.$refs.refFormObserver) {
        this.$refs.refFormObserver.reset()
      }
    },
    async handleSubmitForm() {
      const isValid = await this.$refs.refFormObserver.validate()
      if (!isValid) {
        this.$refs.toast.error('Please complete all required fields')
        return
      }
      if (!this.formData.cities_id) {
        this.$refs.toast.error('Seleccione una ciudad de la lista.')
        return
      }
      await this.onSubmit()
    },
    async onSubmit() {
      this.submitting = true
      try {
        const payload = {
          name: this.formData.name,
          lastname: this.formData.lastname,
          phone: this.formData.phone.replace(/[() -]/g, ''),
          date_birth: this.formData.date_birth,
          email: this.formData.email || null,
          gender: this.formData.gender,
          nameInsuranceId: this.formData.nameInsuranceId,
          user_references_id: this.formData.user_references_id,
          address: this.formData.address || null,
          city: this.formData.cities_id,
          zip_codes_id: this.formData.zip_codes_id,
          copay: Number(this.formData.copay),
          co_insurance: Number(this.formData.co_insurance),
          deducible: Number(this.formData.deducible),
          out_pocket: Number(this.formData.out_pocket),
          member_id: this.formData.member_id,
          confirm_number: this.formData.confirm_number,
        }

        const response = await axiosPatient.createPatientByAgent(payload)
        const { type, message } = notificationCheck(response)
        this.$refs.toast[type](message)

        if (type !== 'error') {
          this.isModalVisible = false
          this.resetForm()
          this.loadPatients()
        }
      } catch (error) {
        const { type, message } = notificationCheck(error.response)
        this.$refs.toast[type](message)
      } finally {
        this.submitting = false
      }
    },
    formatPhone(phone) {
      if (!phone) return ''
      return phone
        .toString()
        .replace(/\D+/g, '')
        .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
  },
  setup() {
    const { refFormObserver, getValidationState: originalGetValidationState, resetForm } =
      formValidation(() => {})

    return {
      refFormObserver,
      resetForm,
    }
  },
}
</script>

<style lang="scss" scoped>
.agent-patient-management {
  background-color: #f4f6f8;
  min-height: 100vh;
  padding: 2rem 0;
}

.flatpickr-wrapper {
  width: 100% !important;
  
  :deep(.flatpickr-input) {
    width: 100% !important;
  }
  
  :deep(input.form-control) {
    width: 100% !important;
  }
}

.is-invalid {
  border-color: #dc3545 !important;
}

.custom-treeselect {
  :deep(.vue-treeselect__control) {
    border-radius: 8px;
    border: 2px solid #e1e5e9;
    transition: all 0.3s ease;
    font-size: 14px;
    padding: 8px 12px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    min-height: 48px;

    &:hover {
      border-color: #007bff;
    }
  }

  :deep(.vue-treeselect__control--focused) {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }

  :deep(.vue-treeselect__placeholder) {
    color: #6c757d;
    font-style: italic;
  }

  :deep(.vue-treeselect__menu) {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e1e5e9;
    max-height: 200px;
    overflow-y: auto;
  }

  :deep(.vue-treeselect__option) {
    padding: 10px 16px;
    font-size: 14px;
    line-height: 1.4;
    transition: background-color 0.2s ease;
  }

  :deep(.vue-treeselect__option:hover) {
    background-color: #f8f9fa;
  }

  :deep(.vue-treeselect__option--selected) {
    background-color: #007bff;
    color: white;
  }

  :deep(.vue-treeselect__input) {
    font-size: 14px;
    padding: 4px 0;
  }

  :deep(.vue-treeselect__single-value) {
    font-size: 14px;
    color: #495057;
  }
}

.custom-treeselect.is-invalid {
  :deep(.vue-treeselect__control) {
    border-color: #dc3545 !important;
    border-width: 2px !important;
  }

  :deep(.vue-treeselect__control--focused) {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }
}

/* Mismo comportamiento visual que PatientAdd.vue (lista bajo el input) */
.city-autocomplete-wrap {
  position: relative;
}

.city-options-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 2055;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
}

.city-option-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}

.city-option-item:hover {
  background-color: #f8f9fa;
}

.city-option-item:last-child {
  border-bottom: none;
}
</style>

<style lang="scss">
/*
 * b-modal: dialog-class en .modal-dialog.
 * La lista de ciudades (position absolute) quedaba tapada por .modal-footer (mismo stacking, DOM posterior)
 * y/o recortada por overflow del modal — en PatientAdd.vue el sidebar no tiene ese footer encima.
 */
.modal-agent-patient-wide {
  max-width: 1240px !important;
  width: 96vw;
}

.modal-agent-patient-wide .modal-content {
  overflow: visible !important;
}

.modal-agent-patient-wide .modal-body {
  overflow: visible !important;
  position: relative;
  z-index: 2;
}

.modal-agent-patient-wide .modal-footer {
  position: relative;
  z-index: 1;
  background-color: #fff;
}

.agent-appointments-modal .modal-header {
  background: #f7f4ef;
  border-bottom: 1px solid #eadfce;
}

.agent-appointments-modal .modal-title {
  color: #3c231d;
  font-weight: 700;
}

.agent-appointments-table thead th {
  background: #f3efe8;
  color: #6b5344;
  font-size: 0.78rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  border-color: #eadfce;
}

.agent-appointments-table td {
  vertical-align: middle;
  border-color: #f0e6da;
}

.visit-type-badge {
  min-width: 4.5rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.45rem 0.7rem;
}

.visit-date-pill {
  display: inline-block;
  background: #eef5fb;
  color: #1d4f73;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-weight: 600;
  font-size: 0.85rem;
}

.visit-reason-pill {
  display: inline-block;
  background: #f8f1ea;
  color: #6b4f3a;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.85rem;
}

.facility-label {
  color: #1a6fb5;
  font-weight: 600;
}
</style>

